import React, { useEffect, useState } from 'react';
import { WidgetProps } from '@rjsf/utils';
import { TextField, Autocomplete } from '@mui/material';
import { INPUT_STYLE } from '../../../../../utils/styleHelpers';
import { DetailRow } from '../../../DetailsDrawer/DetailRow';

export const SelectInput = (props: WidgetProps) => {
  const { options, required, value, onChange } = props;
  const [searchTerm, setSearchTerm] = useState(value || '');
  const [dirty, setDirty] = useState(false);
  // Preserve empty strings, don't convert to null
  const [selectedStatus, setSelectedStatus] = useState<string | null>(props.value ?? null);
  const [optionsList, setOptionsList] = useState<string[]>(
    (options?.enumOptions || [])
      .filter((opt: { label?: string; value: string }) => opt.label !== 'null' && opt.value !== null)
      .map((opt: { label?: string; value: string }) => opt.label || opt.value) || []
  );

  // On mount, if value is empty string and field is optional, convert to undefined
  useEffect(() => {
    if (!required && value === '') {
      onChange(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (_e: React.SyntheticEvent<Element, Event>, value: string | null) => {
    setSelectedStatus(value || null);
    setSearchTerm(value || '');
    // Convert null or empty string to undefined
    onChange(value === null || value === '' ? undefined : value);
  };

  const handleInputChange = (_e: React.SyntheticEvent<Element, Event>, value: string | null) => {
    if (!dirty) setDirty(true);
    setSelectedStatus(value || null);
    setSearchTerm(value || '');
    // Convert null or empty string to undefined
    onChange(value === null || value === '' ? undefined : value);
  };

  const getHelperText = () => {
    if (isErrorState()) {
      if (props.rawErrors?.includes('must be string')) return `${props.uiSchema?.['ui:placeholder']} is required`;
      if (props.rawErrors?.some((e) => e.includes('must match pattern'))) {
        if (value === '' || value === undefined) return `${props.uiSchema?.['ui:placeholder']} is required`;
        return `${props.uiSchema?.['ui:placeholder']} must be alphanumeric, please remove special characters`;
      }
    }
  };

  const isErrorState = () => {
    // If there are raw errors, it's an error state (regardless of required status)
    if (dirty && props.rawErrors && props.rawErrors.length > 0) {
      return true;
    }
    // If field is not required and has no value (null, undefined, or empty string), it's not an error
    if (!required && (value === null || value === undefined || value === '')) {
      return false;
    }
    return dirty && !value && required;
  };

  useEffect(() => {
    if (value) {
      setSelectedStatus(value);
      setSearchTerm(value);
    }
  }, [value]);

  useEffect(() => {
    setOptionsList(
      (options?.enumOptions || [])
        .filter((opt: { label?: string; value: string }) => opt.label !== 'null' && opt.value !== null)
        .map((opt: { label?: string; value: string }) => opt.label || opt.value) || []
    );
  }, [options]);

  if (props.readonly) {
    if (props.formContext?.hideEmpty && !value) return null;

    return (
      <div>
        <DetailRow
          key={props.id}
          colId={props.id}
          title={props.uiSchema?.['ui:placeholder'] || ''}
          value={value}
        />
      </div>
    );
  }

  return (
    <Autocomplete
      value={selectedStatus || searchTerm || ''}
      inputValue={searchTerm || ''}
      onInputChange={handleInputChange}
      ListboxProps={{ style: { maxHeight: 150 } }}
      onChange={handleChange}
      onBlur={() => setDirty(true)}
      onKeyDown={(e) => e.key === 'Enter' && e.preventDefault()}
      options={optionsList}
      getOptionLabel={(option) => option || ''}
      renderInput={(params) => (
        <TextField
          {...params}
          required={props.required}
          error={isErrorState()}
          helperText={getHelperText()}
          label={props.uiSchema?.['ui:placeholder']}
          InputProps={{
            ...params.InputProps,
            style: { fontSize: '.875rem' },
            name: props.uiSchema?.['ui:placeholder']
          }}
          className={INPUT_STYLE}
        />
      )}
      renderOption={(props, option) => (
        <li
          {...props}
          key={option}>
          {option}
        </li>
      )}
    />
  );
};

export default SelectInput;
